import React, { useState, useEffect } from 'react';
import {
  BuildingOfficeIcon,
  GlobeAltIcon,
  MapPinIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { ReminderProfileEditor } from './ReminderProfileEditor';
import type { TaskReminderProfile } from '../../../types/taskReminders';
import { serviceAreaConfigService } from '../../../services/serviceAreaConfigService';
import { updateSystemPreferences } from '../services/SystemPreferencesAPI';

// Types for subscriber profile data matching the inherited backend structure.
interface FirmSettings {
  timezone?: string;
  dateFormat?: string;
  currency?: string;
  language?: string;
  taskReminders?: TaskReminderProfile;
  [key: string]: any;
}

interface MatterNumberingConfig {
  template: 'standard' | 'custom';
  format: string;
  startingNumber: number;
  includeClientNumber: boolean;
  separator: string;
  customFields: string[];
  updateExistingMatters: boolean;
}

interface FirmData {
  id?: string;
  firmName: string;
  displayName: string;
  practiceAreas: string[];
  licensedStates: string[];
  matterNumberingConfig: MatterNumberingConfig;
  firmSettings: FirmSettings;
  keycloakRealmId?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface PracticeArea {
  id: string;
  name: string;
  displayName: string;
  isActive: boolean;
}

interface FormData {
  firmName: string;
  displayName: string;
  practiceAreas: string[];
  licensedStates: string[];
  firmSettings: {
    timezone: string;
    dateFormat: string;
    currency: string;
    language: string;
    taskReminders?: TaskReminderProfile;
  };
}

interface FirmInformationPanelProps {
  onSave?: (data: FormData) => void;
}

const US_STATES: Array<{ code: string; name: string }> = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

const FirmInformationPanel: React.FC<FirmInformationPanelProps> = ({ onSave }) => {
  const [formData, setFormData] = useState<FormData>({
    firmName: '',
    displayName: '',
    practiceAreas: [],
    licensedStates: [],
    firmSettings: {
      timezone: 'America/New_York',
      dateFormat: 'MM/DD/YYYY',
      currency: 'USD',
      language: 'en',
      taskReminders: undefined
    }
  });

  const [availablePracticeAreas, setAvailablePracticeAreas] = useState<PracticeArea[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Form validation state
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [reminderProfileErrors, setReminderProfileErrors] = useState<string[]>([]);
  
  // Operating context search
  const [stateSearchQuery, setStateSearchQuery] = useState('');

  // Change confirmation modal state
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [changesSummary, setChangesSummary] = useState<Array<{field: string, oldValue: any, newValue: any}>>([]);
  const [originalFormData, setOriginalFormData] = useState<FormData | null>(null);

  // Load subscriber data and service areas on component mount
  useEffect(() => {
    loadFirmData();
    loadPracticeAreas();
  }, []);

  // Debug: Track success state changes
  useEffect(() => {
    console.log('🎉 RENDER: Success message state changed to:', success);
  }, [success]);

  const loadFirmData = async () => {
    try {
      setLoading(true);
      setError(null);

      const subscriberData = await serviceAreaConfigService.getSubscriberSettings();

      const loadedFormData = {
        firmName: subscriberData.firmName || '',
        displayName: subscriberData.displayName || '',
        practiceAreas: subscriberData.practiceAreas || [],
        licensedStates: subscriberData.licensedStates || [],
        firmSettings: {
          timezone: subscriberData.firmSettings?.timezone || 'America/New_York',
          dateFormat: subscriberData.firmSettings?.dateFormat || 'MM/DD/YYYY',
          currency: subscriberData.firmSettings?.currency || 'USD',
          language: subscriberData.firmSettings?.language || 'en',
          taskReminders: subscriberData.firmSettings?.taskReminders as TaskReminderProfile | undefined
        }
      };

      setFormData(loadedFormData);
      setOriginalFormData(loadedFormData); // Store original for change comparison
      setHasChanges(false);
    } catch (err) {
      console.error('Error loading subscriber profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to load subscriber profile');
    } finally {
      setLoading(false);
    }
  };

  const loadPracticeAreas = async () => {
    try {
      const practiceAreas: PracticeArea[] = await serviceAreaConfigService.getAvailableServiceAreas();
      setAvailablePracticeAreas(practiceAreas);
    } catch (err) {
      console.error('Error loading service areas:', err);
    }
  };

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};

    if (!formData.firmName.trim()) {
      errors.firmName = 'Business name is required';
    } else if (formData.firmName.length < 2) {
      errors.firmName = 'Business name must be at least 2 characters';
    }

    if (!formData.displayName.trim()) {
      errors.displayName = 'Display name is required';
    } else if (formData.displayName.length < 2) {
      errors.displayName = 'Display name must be at least 2 characters';
    }

    if (formData.practiceAreas.length === 0) {
      errors.practiceAreas = 'At least one service area must be selected';
    }

    if (formData.licensedStates.length === 0) {
      errors.licensedStates = 'At least one operating context must be selected';
    }

    if (reminderProfileErrors.length > 0) {
      errors.taskReminders = 'Reminder profile has validation errors. Please fix them before saving.';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    if (field === 'firmSettings') {
      setFormData(prev => ({
        ...prev,
        firmSettings: { ...prev.firmSettings, ...value }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    setHasChanges(true);
    setSuccess(null);
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handlePracticeAreaToggle = (practiceAreaName: string) => {
    const updatedAreas = formData.practiceAreas.includes(practiceAreaName)
      ? formData.practiceAreas.filter(area => area !== practiceAreaName)
      : [...formData.practiceAreas, practiceAreaName];

    handleInputChange('practiceAreas', updatedAreas);
  };

  const handleStateToggle = (stateCode: string) => {
    const updated = formData.licensedStates.includes(stateCode)
      ? formData.licensedStates.filter(s => s !== stateCode)
      : [...formData.licensedStates, stateCode];
    handleInputChange('licensedStates', updated);
  };

  const handleStateRemove = (stateCode: string) => {
    handleInputChange('licensedStates', formData.licensedStates.filter(s => s !== stateCode));
  };

  const filteredStates = US_STATES.filter(state =>
    state.name.toLowerCase().includes(stateSearchQuery.toLowerCase()) ||
    state.code.toLowerCase().includes(stateSearchQuery.toLowerCase())
  );

  const calculateChanges = (original: FormData | null, current: FormData): Array<{field: string, oldValue: any, newValue: any}> => {
    if (!original) return [];
    
    const changes: Array<{field: string, oldValue: any, newValue: any}> = [];
    
    // Check business name
    if (original.firmName !== current.firmName) {
      changes.push({
        field: 'Business Legal Name',
        oldValue: original.firmName || '(empty)',
        newValue: current.firmName || '(empty)'
      });
    }
    
    // Check display name
    if (original.displayName !== current.displayName) {
      changes.push({
        field: 'Display Name',
        oldValue: original.displayName || '(empty)',
        newValue: current.displayName || '(empty)'
      });
    }
    
    // Check practice areas
    const originalAreas = [...original.practiceAreas].sort().join(', ');
    const currentAreas = [...current.practiceAreas].sort().join(', ');
    if (originalAreas !== currentAreas) {
      changes.push({
        field: 'Service Areas',
        oldValue: originalAreas || '(none selected)',
        newValue: currentAreas || '(none selected)'
      });
    }

    // Check licensed states
    const originalStates = [...(original.licensedStates || [])].sort().join(', ');
    const currentStates = [...(current.licensedStates || [])].sort().join(', ');
    if (originalStates !== currentStates) {
      changes.push({
        field: 'Operating Contexts',
        oldValue: originalStates || '(none selected)',
        newValue: currentStates || '(none selected)'
      });
    }

    // Check timezone
    if (original.firmSettings.timezone !== current.firmSettings.timezone) {
      changes.push({
        field: 'Timezone',
        oldValue: original.firmSettings.timezone,
        newValue: current.firmSettings.timezone
      });
    }
    
    // Check date format
    if (original.firmSettings.dateFormat !== current.firmSettings.dateFormat) {
      changes.push({
        field: 'Date Format',
        oldValue: original.firmSettings.dateFormat,
        newValue: current.firmSettings.dateFormat
      });
    }
    
    // Check currency
    if (original.firmSettings.currency !== current.firmSettings.currency) {
      changes.push({
        field: 'Currency',
        oldValue: original.firmSettings.currency,
        newValue: current.firmSettings.currency
      });
    }

    // Check reminder profile
    const originalReminders = JSON.stringify(original.firmSettings.taskReminders || null);
    const currentReminders = JSON.stringify(current.firmSettings.taskReminders || null);
    if (originalReminders !== currentReminders) {
      changes.push({
        field: 'Task Reminder Defaults',
        oldValue: original.firmSettings.taskReminders ? 'Configured' : 'Not configured',
        newValue: current.firmSettings.taskReminders ? 'Configured' : 'Not configured'
      });
    }
    
    return changes;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    // Calculate changes and show confirmation modal
    const changes = calculateChanges(originalFormData, formData);
    setChangesSummary(changes);
    setShowChangeModal(true);
  };

  const confirmAndSaveChanges = async () => {
    try {
      setSaving(true);
      setError(null);
      setShowChangeModal(false);

      await updateSystemPreferences(serviceAreaConfigService.toSystemPreferencesUpdate(formData));

      console.log('🎉 SUCCESS: Form saved successfully, setting success message');

      // Show success message
      setSuccess('Subscriber profile updated successfully.');
      setHasChanges(false);
      setOriginalFormData(formData); // Update original data for future comparisons
      serviceAreaConfigService.clearCache();

      if (onSave) {
        onSave(formData);
      }

      console.log('🎉 SUCCESS: Success message set, will auto-hide in 5 seconds');
      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        console.log('🎉 SUCCESS: Auto-hiding success message');
        setSuccess(null);
      }, 5000);
    } catch (err) {
      console.error('Error saving subscriber profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to save subscriber profile');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    loadFirmData();
    setValidationErrors({});
    setError(null);
    setSuccess(null);
    setReminderProfileErrors([]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center space-x-2 text-lex-accent">
          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
          </svg>
          <span className="font-medium">Loading subscriber profile...</span>
        </div>
      </div>
    );
  }

  const timezoneOptions = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  ];

  const dateFormatOptions = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2024)' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/12/2024)' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2024-12-31)' },
    { value: 'MMM DD, YYYY', label: 'MMM DD, YYYY (Dec 31, 2024)' },
  ];

  const currencyOptions = [
    { value: 'USD', label: 'US Dollar ($)' },
    { value: 'EUR', label: 'Euro (€)' },
    { value: 'GBP', label: 'British Pound (£)' },
    { value: 'CAD', label: 'Canadian Dollar (C$)' },
  ];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Status Messages */}
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400 mr-2" />
            <span className="text-sm text-red-800 dark:text-red-200">{error}</span>
          </div>
        </div>
      )}

      {success && (
        <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
            <span className="text-sm text-green-800 dark:text-green-200">{success}</span>
          </div>
        </div>
      )}

      {/* Main Form */}
      <div className="space-y-8">
        
        {/* Basic Subscriber Information */}
        <div className="bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <BuildingOfficeIcon className="h-5 w-5 text-lex-accent mr-2" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Basic Information
              </h3>
            </div>
          </div>
          
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Business Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Business Name *
                </label>
                <input
                  type="text"
                  value={formData.firmName}
                  onChange={(e) => handleInputChange('firmName', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lex-accent focus:border-lex-accent transition-colors ${
                    validationErrors.firmName 
                      ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20' 
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'
                  } text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
                  placeholder="Enter the subscriber business name"
                />
                {validationErrors.firmName && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors.firmName}</p>
                )}
              </div>

              {/* Display Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Display Name *
                </label>
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={(e) => handleInputChange('displayName', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lex-accent focus:border-lex-accent transition-colors ${
                    validationErrors.displayName 
                      ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20' 
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'
                  } text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
                  placeholder="Name displayed in the application"
                />
                {validationErrors.displayName && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors.displayName}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Service Areas */}
        <div className="bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <GlobeAltIcon className="h-5 w-5 text-lex-accent mr-2" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Service Areas *
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Select the kinds of work this subscriber performs.
            </p>
          </div>
          
          <div className="p-6">
            {availablePracticeAreas.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {availablePracticeAreas.map((area) => (
                  <label
                    key={area.id}
                    className="flex items-center p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={formData.practiceAreas.includes(area.displayName)}
                      onChange={() => handlePracticeAreaToggle(area.displayName)}
                      className="h-4 w-4 text-lex-accent border-gray-300 dark:border-gray-600 rounded focus:ring-lex-accent"
                    />
                    <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                      {area.displayName}
                    </span>
                  </label>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 dark:text-gray-500 text-sm">
                  Loading service areas...
                </div>
              </div>
            )}
            
            {validationErrors.practiceAreas && (
              <p className="mt-3 text-sm text-red-600 dark:text-red-400">{validationErrors.practiceAreas}</p>
            )}
          </div>
        </div>

        {/* Operating Contexts */}
        <div className="bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <MapPinIcon className="h-5 w-5 text-lex-accent mr-2" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Operating Contexts *
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Select the regions or operating contexts that should influence templates, defaults, and future capability packs.
            </p>
          </div>

          <div className="p-6">
            {/* Selected states as removable pills */}
            {formData.licensedStates.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {formData.licensedStates.map(code => {
                  const state = US_STATES.find(s => s.code === code);
                  return (
                    <span
                      key={code}
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-lex-accent-soft text-lex-accent rounded-full text-sm"
                    >
                      {state?.name || code}
                      <button
                        type="button"
                        onClick={() => handleStateRemove(code)}
                        className="ml-0.5 text-lex-accent hover:text-lex-accent-hover"
                      >
                        <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                        </svg>
                      </button>
                    </span>
                  );
                })}
              </div>
            )}

            {/* Search input */}
            <input
              type="text"
              placeholder="Search states..."
              value={stateSearchQuery}
              onChange={(e) => setStateSearchQuery(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-lex-accent focus:border-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 text-sm"
            />

            {/* Scrollable checkbox list */}
            <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg mt-2">
              {filteredStates.map(state => (
                <label
                  key={state.code}
                  className="flex items-center px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                >
                  <input
                    type="checkbox"
                    checked={formData.licensedStates.includes(state.code)}
                    onChange={() => handleStateToggle(state.code)}
                    className="h-4 w-4 text-lex-accent border-gray-300 dark:border-gray-600 rounded focus:ring-lex-accent"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    {state.name} ({state.code})
                  </span>
                </label>
              ))}
              {filteredStates.length === 0 && (
                <div className="px-3 py-4 text-sm text-gray-400 dark:text-gray-500 text-center">
                  No states match your search
                </div>
              )}
            </div>

            {/* Quick actions + count */}
            <div className="flex items-center justify-between mt-3">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleInputChange('licensedStates', US_STATES.map(s => s.code))}
                  className="text-xs text-lex-accent hover:text-lex-accent-hover"
                >
                  Select All
                </button>
                <span className="text-gray-300 dark:text-gray-600">|</span>
                <button
                  type="button"
                  onClick={() => handleInputChange('licensedStates', [])}
                  className="text-xs text-lex-accent hover:text-lex-accent-hover"
                >
                  Clear All
                </button>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {formData.licensedStates.length} selected
              </span>
            </div>

            {validationErrors.licensedStates && (
              <p className="mt-3 text-sm text-red-600 dark:text-red-400">{validationErrors.licensedStates}</p>
            )}
          </div>
        </div>

        {/* System Preferences */}
        <div className="bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <GlobeAltIcon className="h-5 w-5 text-lex-accent mr-2" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                System Preferences
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Configure regional and display settings
            </p>
          </div>
          
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Timezone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Timezone
                </label>
                <select
                  value={formData.firmSettings.timezone}
                  onChange={(e) => handleInputChange('firmSettings', { timezone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-lex-accent focus:border-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {timezoneOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Format */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Date Format
                </label>
                <select
                  value={formData.firmSettings.dateFormat}
                  onChange={(e) => handleInputChange('firmSettings', { dateFormat: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-lex-accent focus:border-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {dateFormatOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Currency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Currency
                </label>
                <select
                  value={formData.firmSettings.currency}
                  onChange={(e) => handleInputChange('firmSettings', { currency: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-lex-accent focus:border-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {currencyOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Language */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Language
                </label>
                <select
                  value={formData.firmSettings.language}
                  onChange={(e) => handleInputChange('firmSettings', { language: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-lex-accent focus:border-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Task Reminder Defaults */}
        <div className="bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 text-lex-accent mr-2" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Task Reminder Defaults
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Configure subscriber-wide reminder rules used when users have not overridden their own profile.
            </p>
          </div>
          <div className="p-6">
            <ReminderProfileEditor
              value={formData.firmSettings.taskReminders || null}
              onChange={(nextProfile) => handleInputChange('firmSettings', { taskReminders: nextProfile })}
              title="Subscriber Default Reminder Profile"
              description="Uses the subscriber timezone above unless a user overrides their own timezone."
              allowClear={false}
              showTimezone={false}
              fallbackTimezone={formData.firmSettings.timezone}
              onValidationChange={(_isValid, errors) => setReminderProfileErrors(errors)}
            />
            {validationErrors.taskReminders && (
              <div className="mt-3 text-sm text-red-600 dark:text-red-400">
                {validationErrors.taskReminders}
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center pt-6 border-t border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {hasChanges && (
              <span className="flex items-center">
                <div className="h-2 w-2 bg-amber-400 rounded-full mr-2"></div>
                Unsaved changes
              </span>
            )}
          </div>
          
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={handleReset}
              disabled={!hasChanges || saving}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-2 focus:ring-lex-accent focus:border-lex-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Reset
            </button>
            
            <button
              type="button"
              onClick={handleSave}
              disabled={!hasChanges || saving || reminderProfileErrors.length > 0}
              className="px-6 py-2 text-sm font-medium text-lex-accent-contrast bg-lex-accent hover:bg-lex-accent-hover focus:ring-2 focus:ring-lex-accent focus:ring-offset-2 dark:focus:ring-offset-gray-800 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              {saving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Change Confirmation Modal */}
      {showChangeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-lg w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Confirm Subscriber Profile Changes
              </h3>
              <button
                onClick={() => {
                  setShowChangeModal(false);
                  setChangesSummary([]);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {changesSummary.length > 0 ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Please review the following changes before saving:
                </p>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {changesSummary.map((change, index) => (
                    <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-md p-3">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {change.field}
                      </div>
                      <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                        <div className="flex items-center space-x-2">
                          <span className="text-red-600 dark:text-red-400">From:</span>
                          <span className="font-mono bg-red-50 dark:bg-red-900/20 px-1 rounded">
                            {change.oldValue}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="text-green-600 dark:text-green-400">To:</span>
                          <span className="font-mono bg-green-50 dark:bg-green-900/20 px-1 rounded">
                            {change.newValue}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                    Are these changes correct?
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  No changes were detected in the subscriber profile.
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Do you want to continue anyway?
                </p>
              </div>
            )}

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowChangeModal(false);
                  setChangesSummary([]);
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                No, Go Back
              </button>
              <button
                onClick={confirmAndSaveChanges}
                disabled={saving}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Yes, Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FirmInformationPanel; 
